import { Dispatch, SetStateAction, useCallback } from 'react'
import { useTranslation } from 'react-i18next';
import { useFormInput } from './form';

export type ValidatorName =
  'notEmpty'
  | 'domainName'
  | 'subdomainName'
  | 'url'
  | 'emailAddress'
  | 'onlyDigits'
  | 'lessOrEqualTo'

export type FileValidatorName =
  'notEmpty'

export type Validator = () => {
  isValid: boolean,
  error?: string
}

export type ValidatorsParams = Partial<{
  onlyDigits: OnlyDigitsParams
  lessOrEqualTo: LessOrEqualParams
}>

type SetError = Dispatch<SetStateAction<string>>

/**
 * Проверка правильности заполнения поля формы с учетом всех валидаторов
 * @param validators
 * @param setError
 */
export function useFieldValidators(validators: Validator[], setError: SetError) {
  const validate = useCallback(() => {
    const isValid = validators.every(validator => {
      const check = validator();
      setError(check.isValid ? undefined : check.error)
      return check.isValid;
    });

    return isValid;
  }, [setError, validators]);

  return validate;
}

/**
 * Проверка на валидность всех полей формы
 * @param fields массив полей
 */
export function useFormValidator(fields: ReturnType<typeof useFormInput>[]) {
  const validate = useCallback(() => {
    let isValid = true;
    fields.forEach(field => {
      if (!field.validate()) {
        isValid = false;
      }
    });
    return isValid;
  }, [fields])

  return validate;
}

/**
 * Проверка на заполненность поля
 */
export function useNotEmptyValidator<T>(use: boolean, value: T): Validator {
  const { t } = useTranslation(['main'])
  const check = useCallback(() => {
    if (!use) { return { isValid: true } }
    return {
      isValid: Array.isArray(value) ? value.some(item => !!item) : !!value,
      error: t('forms.errorFieldNoEmpty')
    }
  }, [value, use]);

  return check;
}

/**
 * Проверка на доменное имя второго и выше уровня
 */
export function useDomainNameValidator(use: boolean, value: string): Validator {
  const { t } = useTranslation(['main'])
  const domainRegexp = /([a-zа-яё0-9_\.-]+)\.([a-zа-яё0-9-]+)/;
  const check = useCallback(() => {
    if (!use) { return { isValid: true } }

    return {
      isValid: domainRegexp.test(value),
      error: t('forms.errorFieldDomain')
    }
  }, [value, use]);

  return check;
}

/**
 * Проверка на имя поддомена 3 и выше уровня (часть до точки)
 */
export function useSubdomainNameValidator(use: boolean, value: string): Validator {
  const { t } = useTranslation(['main'])
  const subdomainRegexp = /^[A-Za-z0-9](?:[A-Za-z0-9\-]{0,61}[A-Za-z0-9])?$/;
  const check = useCallback(() => {
    if (!use) { return { isValid: true } }

    return {
      isValid: subdomainRegexp.test(value),
      error: t('forms.errorFieldSubdomain')
    }
  }, [value, use]);

  return check;
}

// URL validation regexp based on https://stackoverflow.com/a/190405
const URL_REGEX = /^(?:(?:http|https):)?(?:(?:\/\/)?(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:])*@)?(?:\[(?:(?:(?:[0-9a-f]{1,4}:){6}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|::(?:[0-9a-f]{1,4}:){5}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){4}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,1}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){3}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,2}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){2}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,3}[0-9a-f]{1,4})?::[0-9a-f]{1,4}:(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,4}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,5}[0-9a-f]{1,4})?::[0-9a-f]{1,4}|(?:(?:[0-9a-f]{1,4}:){0,6}[0-9a-f]{1,4})?::)|v[0-9a-f]+\.[-a-z0-9\._~!\$&'\(\)\*\+,;=:]+)\]|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3}|(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=])*)(?::[0-9]*)?(?:\/(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@]))*)*|\/(?:(?:(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@]))+)(?:\/(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@]))*)*)?|(?:(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@]))+)(?:\/(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@]))*)*|(?!(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@])))(?:\?(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@])|[\u{E000}-\u{F8FF}\u{F0000}-\u{FFFFD}\u{100000}-\u{10FFFD}\/\?])*)?(?:#(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~\u{00A0}-\u{D7FF}\u{F900}-\u{FDCF}\u{FDF0}-\u{FFEF}\u{10000}-\u{1FFFD}\u{20000}-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}\u{50000}-\u{5FFFD}\u{60000}-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}\u{90000}-\u{9FFFD}\u{A0000}-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}\u{D0000}-\u{DFFFD}\u{E1000}-\u{EFFFD}!\$&'\(\)\*\+,;=:@])|[\/\?])*)?$/iu

/**
 * Проверка на валидный URL
 */
export function useUrlValidator(use: boolean, value: string): Validator {
  const { t } = useTranslation(['main'])

  const check = useCallback(() => {
    if (!use) { return { isValid: true } }

    return {
      isValid: URL_REGEX.test(value),
      error: t('forms.errorFieldUrl')
    }
  }, [value, use]);

  return check;
}

// Email validation regexp from HTML5 spec https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const EMAIL_REGEXP = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Проверка на адрес email
 */
export function useEmailAddressValidator(use: boolean, value: string): Validator {
  const { t } = useTranslation(['main'])

  const check = useCallback(() => {
    if (!use) { return { isValid: true } }

    return {
      isValid: EMAIL_REGEXP.test(value),
      error: t('forms.errorFieldEmail')
    }
  }, [value, use]);

  return check;
}

/**
 * Проверка на ввод только числовых значений
 */
export type OnlyDigitsParams = {
  withDecimals?: boolean,
  allowedLength?: number[]
}
export function useOnlyDigitsValidator(
  use: boolean,
  value: string,
  params: OnlyDigitsParams
): Validator {
  const { t } = useTranslation(['main'])
  const onlyDigitsRegexp = /[^0-9]/;
  const withDecimalsRegexp = /[^0-9\.]/;
  const check = useCallback(() => {
    if (!use) return { isValid: true };

    if (params?.withDecimals ? withDecimalsRegexp.test(value) : onlyDigitsRegexp.test(value)) {
      return {
        isValid: false,
        error: t('forms.errorWrongFormat'),
      }
    }

    if (params?.allowedLength?.length && !params.allowedLength.includes(value.length)) {
      return {
        isValid: false,
        error: params.allowedLength.length > 1 ?
          t('forms.errorWrongNumberLengthMulty', {
            firstCounts: params.allowedLength.slice(0, -1).join(', '),
            count      : params.allowedLength[params.allowedLength.length - 1]
          })
          :
          t('forms.errorWrongNumberLength', { count: params.allowedLength[0] })
      }
    }

    return {
      isValid: true
    }
  }, [value, use])

  return check;
}

/**
 * Значение меньше или равно
 */
export type LessOrEqualParams = {
  value: number,
  error?: string
}
export function useLessOrEqualValidator(
  use: boolean,
  value: string,
  params?: LessOrEqualParams
): Validator {
  const { t } = useTranslation(['main'])
  const check = useCallback(() => {
    if (!use) return { isValid: true };
    const numberedValue = Number(value);

    return {
      isValid: !isNaN(numberedValue) && numberedValue <= params.value,
      error: params.error ?? t('forms.errorFieldLessOrEqual', { value: params.value })
    }
  }, [value, use, params?.error, params?.value])

  return check;
}
